import React from 'react'
import { useToast } from '../context/ToastContext'
import { gamesApi } from '../api/games'

export default function TopTenGames() {
    const [myGames, setMyGames]       = React.useState(false)
    const [firstName, setFirstName]   = React.useState('')
    const [lastName, setLastName]     = React.useState('')
    const [city, setCity]             = React.useState('')
    const [results, setResults]       = React.useState([])
    const [loading, setLoading]       = React.useState(true)
    const { addToast } = useToast()

    async function fetchResults({ myGamesOverride = myGames, fn = firstName, ln = lastName, ct = city } = {}) {
        setLoading(true)
        try {
            const data = await gamesApi.topTenGames({
                myGames: myGamesOverride,
                firstName: fn,
                lastName: ln,
                city: ct,
            })
            setResults(data)
        } catch (err) {
            addToast(err.message, 'error')
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => { fetchResults() }, []) // eslint-disable-line react-hooks/exhaustive-deps

    function handleSearch(e) {
        e.preventDefault()
        fetchResults()
    }

    function handleToggle() {
        const next = !myGames
        setMyGames(next)
        setFirstName('')
        setLastName('')
        setCity('')
        fetchResults({ myGamesOverride: next, fn: '', ln: '', ct: '' })
    }

    const inputClass = "w-full px-3 py-2 rounded-md border border-gray-300 font-karla text-sm text-bankroll-text focus:outline-none focus:ring-2 focus:ring-bankroll-primary"
    const labelClass = "block text-xs font-bold text-gray-300 uppercase tracking-wide mb-1"

    return (
        <main className="page-main">
            <div className="flex justify-end">
                <button
                    onClick={handleToggle}
                    className="px-4 py-2 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85"
                >
                    {myGames ? 'Global' : 'My Games'}
                </button>
            </div>

            <div className="w-full">
                {!myGames && (
                    <form onSubmit={handleSearch} className="w-full bg-white rounded-[10px] px-5 py-5 shadow-sm flex flex-col gap-4 mb-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className={labelClass}>First Name</label>
                                <input className={inputClass} value={firstName} onChange={e => setFirstName(e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>Last Name</label>
                                <input className={inputClass} value={lastName} onChange={e => setLastName(e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>City</label>
                                <input className={inputClass} value={city} onChange={e => setCity(e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="px-6 py-2 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85"
                            >
                                Search
                            </button>
                        </div>
                    </form>
                )}

                {loading ? (
                    <p className="text-gray-400 text-[0.95rem]">Loading…</p>
                ) : results.length === 0 ? (
                    <p className="text-gray-400 text-[0.95rem]">No completed games found.</p>
                ) : (
                    <>
                        {/* Mobile: card list */}
                        <ul className="sm:hidden list-none m-0 p-0 flex flex-col gap-3">
                            {results.map(entry => (
                                <li key={`${entry.game_id}-${entry.rank}`} className="bg-white rounded-[10px] shadow-sm px-4 py-3 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-lg font-bold text-bankroll-primary shrink-0">#{entry.rank}</span>
                                        <div className="flex flex-col gap-0.5 min-w-0">
                                            <span className="font-bold text-bankroll-text text-sm truncate">
                                                {entry.first_name} {entry.last_name}
                                            </span>
                                            <span className="text-xs text-gray-400 truncate">
                                                {[entry.city, entry.completed_at ? new Date(entry.completed_at).toLocaleDateString() : null].filter(Boolean).join(' · ')}
                                            </span>
                                        </div>
                                    </div>
                                    <span className="shrink-0 text-sm font-bold text-bankroll-text">{entry.total_score}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Desktop: table */}
                        <div className="hidden sm:block w-full bg-white rounded-[10px] shadow-sm overflow-x-auto">
                            <table className="min-w-full border-collapse" style={{ gridTemplateColumns: 'repeat(6, minmax(60px, 1fr))' }}>
                                <thead>
                                    <tr className="bg-bankroll-primary">
                                        <th className="text-left px-3 py-2.5 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">#</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">First</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">Last</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">City</th>
                                        <th className="text-left px-3 py-2.5 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">Date</th>
                                        <th className="text-right px-3 py-2.5 text-xs font-bold text-white uppercase tracking-wide whitespace-nowrap">Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map(entry => (
                                        <tr key={`${entry.game_id}-${entry.rank}`} className="border-b border-gray-100 last:border-0">
                                            <td className="px-3 py-2.5 text-sm font-bold text-bankroll-primary whitespace-nowrap">{entry.rank}</td>
                                            <td className="px-3 py-2.5 text-sm text-bankroll-text whitespace-nowrap">{entry.first_name}</td>
                                            <td className="px-3 py-2.5 text-sm text-bankroll-text whitespace-nowrap">{entry.last_name}</td>
                                            <td className="px-3 py-2.5 text-sm text-bankroll-text whitespace-nowrap">{entry.city}</td>
                                            <td className="px-3 py-2.5 text-sm text-bankroll-text whitespace-nowrap">{entry.completed_at ? new Date(entry.completed_at).toLocaleDateString() : '—'}</td>
                                            <td className="px-3 py-2.5 text-sm font-bold text-bankroll-text text-right whitespace-nowrap">{entry.total_score}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </main>
    )
}
